import React, { useCallback, useMemo, useSyncExternalStore } from 'react';
import { getAllSegments } from '@goldcast/player-config/src/utils/clipUtils';
import { getDeletesForSegmentPure } from '@goldcast/player-config/src/utils/timeUtils';
import { approxGTE, approxLTE } from '@goldcast/player-config/src/utils/compareUtils';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import PlayerTimelineButton from './PlayerTimelineButton';
import { getPlayerTimeFromSegmentTime } from '../timeUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtils/constants';
import { updateClipData } from '../clipUpdateDataUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { defaultTimelineSelectionState, transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';

export default function DeleteSelected({ isDisabled = false }: { isDisabled?: boolean }) {
  const { startTime, endTime, isSelecting, startSegmentId, endSegmentId, isTimelineSelect } = useSyncExternalStore(
    transcriptTimelineSelectionStore.subscribe,
    transcriptTimelineSelectionStore.getSnapshot
  );

  const { seekTo } = useRemotionPlayerSeek();

  const { clipData, clipId } = useClipsContext();

  const shouldShowDeleteButton = useMemo(() => {
    if (!isSelecting && startTime !== null && endTime !== null && startSegmentId !== null && endSegmentId !== null) {
      const allSegments = getAllSegments(clipData);
      const startSegment = allSegments.find(segment => segment.id === startSegmentId);
      if (!startSegment) {
        return false;
      }
      const endSegment = allSegments.find(segment => segment.id === endSegmentId);
      if (!endSegment) {
        return false;
      }

      if (startSegment.type === SegmentType.VIDEO || endSegment.type === SegmentType.VIDEO) {
        return false;
      }

      if (startSegmentId !== endSegmentId) {
        return true;
      }

      const deletes = getDeletesForSegmentPure({
        clip: clipData,
        segmentId: startSegmentId,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });

      if (!deletes) {
        return true;
      }

      const isTotallyEnclosedByDelete = Object.values(deletes).some(deleteItem => {
        const deleteStart = deleteItem.bounds[0];
        const deleteEnd = deleteItem.bounds[1];

        // Check if the selected time range is completely within a delete range
        return approxGTE(startTime, deleteStart) && approxLTE(endTime, deleteEnd);
      });

      return !isTotallyEnclosedByDelete;
    }
    return false;
    // Start and EndTime are not added here to prevent unwanted calculations since we are not showing the button while dragging
  }, [isSelecting, startTime, endTime, startSegmentId, endSegmentId, clipData]);

  const clickHandler = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      if (startTime === null || endTime === null) {
        return;
      }

      const newClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
        startTime: startTime,
        startSegmentId: startSegmentId || '',
        endSegmentId: endSegmentId || '',
        endTime: endTime,
        deleteType: 'MANUAL',
        isTimelineSelection: !!isTimelineSelect
      });

      if (startSegmentId) {
        seekTo(getPlayerTimeFromSegmentTime(startTime, newClipData, startSegmentId));
      }
      transcriptTimelineSelectionStore.set(() => ({
        ...defaultTimelineSelectionState
      }));
    },
    [clipId, endSegmentId, endTime, isTimelineSelect, seekTo, startSegmentId, startTime]
  );

  if (!shouldShowDeleteButton) {
    return null;
  }

  return (
    <PlayerTimelineButton
      iconName="IconTrash"
      onClick={clickHandler}
      onMouseDown={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
      tooltipTitle="Delete"
      buttonId="delete-selected"
      isDisabled={isDisabled}
    />
  );
}
